import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import {
  Missing,
  BoolCell,
  CountryCell,
  DateCell,
  getCheckboxColumnDefs,
  TextCell,
  QRCodeCell,
} from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { LastPendingIrContactCell } from "~/components/cells/LastPendingIrContactCell.tsx"
import { SupplierCell } from "~/components/cells/SupplierCell.tsx"
import { COUNTRIES } from "~/utils/countries"

const columnHelper = createColumnHelper<LibraryResponses.Product>()

export const useAgecColumns = () => {
  const { t } = useTranslation("products")

  return useMemo<ColumnDef<LibraryResponses.Product, any>[]>(
    () => [
      getCheckboxColumnDefs<LibraryResponses.Product>(),
      columnHelper.accessor("name", {
        id: "name",
        header: t("lists.columns.name"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),

      columnHelper.accessor("reference", {
        id: "reference",
        header: t("lists.columns.reference"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("purchaseOrder.reference", {
        id: "purchaseOrder",
        header: t("lists.columns.purchaseOrder"),
        enableSorting: false,
        cell: (info) => (
          <TextCell value={info.row.original.purchaseOrder?.reference} />
        ),
      }),
      columnHelper.accessor("ingestedAt", {
        id: "ingestedAt",
        header: t("lists.columns.ingestedAt"),
        enableSorting: false,
        cell: ({ getValue }) => <DateCell date={getValue()} />,
      }),
      columnHelper.accessor("agecCompliance", {
        header: t("lists.columns.agecCompliance"),
        enableSorting: false,
        id: "agecCompliance",
        cell: (info) => <BoolCell value={info.getValue()} />,
      }),
      columnHelper.accessor(
        ({ agecActivityCountries }) => agecActivityCountries[0],
        {
          header: t("lists.columns.agecCountry1"),
          enableSorting: false,
          id: "agecCountry1",
          cell: ({ getValue }) =>
            getValue().country ? (
              <CountryCell
                fw={700}
                countryIso3166={getValue().country.country}
                countriesMapper={COUNTRIES}
              />
            ) : (
              <Missing />
            ),
        }
      ),
      columnHelper.accessor(
        ({ agecActivityCountries }) => agecActivityCountries[1],
        {
          header: t("lists.columns.agecCountry2"),
          enableSorting: false,
          id: "agecCountry2",
          cell: ({ getValue }) => {
            if (!getValue()) return null
            return getValue().country ? (
              <CountryCell
                fw={700}
                countryIso3166={getValue().country.country}
                countriesMapper={COUNTRIES}
              />
            ) : (
              <Missing />
            )
          },
        }
      ),
      columnHelper.accessor(
        ({ agecActivityCountries }) => agecActivityCountries[2],
        {
          header: t("lists.columns.agecCountry3"),
          enableSorting: false,
          id: "agecCountry3",
          cell: (info) => {
            if (!info.getValue()) return null
            return info.getValue()?.country ? (
              <CountryCell
                fw={700}
                countryIso3166={info.getValue().country.country}
                countriesMapper={COUNTRIES}
              />
            ) : (
              <Missing />
            )
          },
        }
      ),
      columnHelper.accessor("recycledMaterial.percentage", {
        header: t("lists.columns.recycledMaterialPercentage"),
        enableSorting: false,
        cell: (info) => {
          return info.getValue() !== undefined ? (
            `${info.getValue()}%`
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("releaseMicroplastics", {
        header: t("lists.columns.releaseMicroplastics"),
        enableSorting: false,
        cell: (info) => {
          const val = info.getValue()
          return val !== "missing-main-component" ? (
            <BoolCell value={val} />
          ) : (
            <BoolCell value={undefined} />
          )
        },
      }),
      columnHelper.accessor("directSupplier", {
        id: "supplier",
        header: t("lists.columns.directSupplier"),
        enableSorting: false,
        cell: (info) => {
          const value = info.row.original.directSupplier
          return <SupplierCell supplierEmail={value.email} />
        },
      }),
      columnHelper.accessor("lastPendingIrContact", {
        header: t("lists.columns.pendingIrContact"),
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue()
          return <LastPendingIrContactCell supplier={value} />
        },
      }),
      columnHelper.accessor("agecShow", {
        id: "agecShow",
        header: t("lists.columns.agecShow"),
        enableSorting: false,
        cell: (info) => (
          <TextCell
            value={
              info.getValue()
                ? t("lists.agecShow.visible")
                : t("lists.agecShow.hidden")
            }
          />
        ),
      }),
      columnHelper.accessor("agecQrCodeUrl", {
        header: undefined,
        id: "action-cell-qrCode",
        enableSorting: false,
        cell: (info) => <QRCodeCell value={info.row.original.agecPopinUrl} />,
      }),
    ],
    [t]
  )
}
